import { Injectable, Injector } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { NotificationService } from '@alfresco/adf-core';
import { ToggleInfoDrawerAction } from '@alfresco/aca-shared/store';

import {
  BatchMetadataUpdateAction,
  BATCH_METADATA_UPDATE,
  ENRICH,
  EnrichAction,
  SnackbarErrorAction,
  SnackbarInfoAction,
  DOWNLOAD_ZIP,
  DownloadZipAction,
} from '../actions/app.custom-actions';
import { CustomContentManagementService } from '../../services/custom-content-management.service';
import { AppStore } from '../states/app.state';
import { getAppSelection } from '../selectors/app.custom-selectors';
import { WkAlfrescoApiService } from '../../services/wk-alfresco-api.service';

interface BatchMetadataUpdateResponse {
  id: string;
  meassageId: string;
  name: string;
}

const jsonContentType = ['application/json'];

@Injectable()
export class NodeBulkOperationsEffects {
  private readonly notificationService: NotificationService;
  private readonly customContentService: CustomContentManagementService;
  private readonly apiService: WkAlfrescoApiService;

  public batchNodesIds: string[];
  public payload: object;
  public zipBlob;
  public linkEle: HTMLAnchorElement;
  public selection;

  constructor(
    injector: Injector,
    private readonly store: Store<AppStore>,
    private readonly actions$: Actions
  ) {
    this.notificationService =
      injector.get<NotificationService>(NotificationService);
    this.customContentService = injector.get<CustomContentManagementService>(
      CustomContentManagementService
    );
    this.apiService = injector.get<WkAlfrescoApiService>(WkAlfrescoApiService);
  }

  downloadZipAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<DownloadZipAction>(DOWNLOAD_ZIP),
        map(() => {
          this.store
            .select(getAppSelection)
            .pipe(take(1))
            .subscribe((selection) => {
              this.selection = selection;
              this.callExportZipAPI();
            });
        })
      ),
    { dispatch: false }
  );

  callExportZipAPI() {
    if (this.selection && !this.selection.isEmpty) {
      this.batchNodesIds = this.selection.nodes.map((node) => node.entry.id);

      this.payload = {
        nodes: this.batchNodesIds.join(),
        fileName: 'UIExport.zip',
      };

      from(
        this.apiService.callContentClientCustomApi({
          path: `alfresco/s/wk/ui/export`,
          httpMethod: 'POST',
          bodyParam: this.payload,
          contentTypes: jsonContentType,
          accepts: jsonContentType,
          returnType: 'blob',
        })
      ).subscribe({
        next: (zipData) => {
          this.zipBlob = new Blob([zipData], { type: 'application/zip' });
          this.linkEle = document.createElement('a');
          this.linkEle.style.display = 'none';
          this.linkEle.href = URL.createObjectURL(this.zipBlob);
          this.linkEle.download = 'UIExport.zip';
          document.body.appendChild(this.linkEle);
          this.linkEle.click();
          URL.revokeObjectURL(this.linkEle.href);
          document.body.removeChild(this.linkEle);
        },
        error: () => {
          this.store.dispatch(
            new SnackbarErrorAction('APP.MESSAGES.ERRORS.DOWNLOADZIP_API_ERROR')
          );
        },
      });
    }
  }

  batchMetadataUpdateAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<BatchMetadataUpdateAction>(BATCH_METADATA_UPDATE),
        map((action) => {
          this.store
            .select(getAppSelection)
            .pipe(take(1))
            .subscribe((selection) => {
              if (selection && !selection.isEmpty && action.payload) {
                const batchMetadataUpdateTitle = action.payload.title || '';
                const batchMetadataUpdateDescription =
                  action.payload.description || '';

                const httpParams = {
                  ids: [],
                  properties: {
                    'cm:title': batchMetadataUpdateTitle,
                    'cm:description': batchMetadataUpdateDescription,
                  },
                };

                const batchMetadataUpdateNodesIds = selection.nodes.map(
                  (node) => node.entry.id
                );

                httpParams['ids'] = batchMetadataUpdateNodesIds;

                from(
                  this.apiService.callContentClientCustomApi({
                    path: `alfresco/service/wk/nodes/cancel-checkout`,
                    httpMethod: 'POST',
                    bodyParam: httpParams,
                    contentTypes: jsonContentType,
                    accepts: jsonContentType,
                  })
                ).subscribe({
                  next: (responseData: Array<BatchMetadataUpdateResponse>) => {
                    const lockedNode = responseData.some(
                      (item) => item.meassageId === 'LOCKED'
                    );

                    if (lockedNode) {
                      this.notificationService.openSnackMessage(
                        'APP.MESSAGES.INFO.METADATA_WAS_NOT_UPDATED',
                        5000
                      );
                    }

                    this.store.dispatch(new ToggleInfoDrawerAction());
                    this.customContentService.checkoutNode();
                  },
                  error: () => {
                    this.notificationService.openSnackMessage(
                      'APP.MESSAGES.ERRORS.BATCH_METADATA_UPDATE_ERROR',
                      3000
                    );
                  },
                });
              }
            });
        })
      ),
    { dispatch: false }
  );

  EnrichAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<EnrichAction>(ENRICH),
        map(() => {
          this.store
            .select(getAppSelection)
            .pipe(take(1))
            .subscribe((selection) => {
              if (selection && !selection.isEmpty) {
                const nodes = selection.nodes.map((node) => node.entry.id);
                const httpParams = { targetNodeRefs: nodes };

                from(
                  this.apiService.callContentClientCustomApi({
                    path: `alfresco/service/wk/nodes/cancel-checkout`,
                    httpMethod: 'POST',
                    bodyParam: httpParams,
                    contentTypes: jsonContentType,
                    accepts: jsonContentType,
                  })
                ).subscribe({
                  next: (data: any) => {
                    this.store.dispatch(
                      new SnackbarInfoAction(
                        'APP.MESSAGES.INFO.ENRICHMENT_REQUESTED',
                        {
                          jobId: data.jobId,
                          unconfigured: data.unconfigured,
                          processed: data.processed,
                          submitted: data.submitted,
                        }
                      )
                    );
                  },
                  error: () => {
                    this.store.dispatch(
                      new SnackbarErrorAction(
                        'APP.MESSAGES.ERRORS.ENRICHMENT_FAILED'
                      )
                    );
                  },
                });
              }
            });
        })
      ),
    { dispatch: false }
  );
}
